import { PETAL_PATH, SAKURA_PATH } from '../core/shapes';
import { lucideIcons, type LucideIconName } from './lucide.generated';

export interface IconDefinition {
  /** Inner SVG markup. */
  body: string;
  /** Defaults to "0 0 24 24". */
  viewBox?: string;
  /** Filled shape (true) or Lucide-style stroke (false, default). */
  filled?: boolean;
}

export type BuiltinIconName = LucideIconName | 'sakura' | 'petal';

const registry = new Map<string, IconDefinition>();
for (const [name, body] of Object.entries(lucideIcons)) registry.set(name, { body });
registry.set('sakura', { body: `<path d="${SAKURA_PATH}"/>`, viewBox: '0 0 100 100', filled: true });
registry.set('petal', { body: `<path d="${PETAL_PATH}"/>`, viewBox: '0 0 100 100', filled: true });

const listeners = new Set<(name: string) => void>();

/**
 * Adds (or replaces) an icon usable as <hn-icon name="…">.
 * Accepts either a full `<svg>` string or a definition object.
 */
export function registerIcon(name: string, icon: IconDefinition | string): void {
  if (typeof icon === 'string') {
    const viewBox = icon.match(/viewBox="([^"]+)"/)?.[1];
    const body = icon.replace(/^[\s\S]*?<svg[^>]*>/, '').replace(/<\/svg>\s*$/, '');
    const filled = /fill="(?!none)[^"]+"/.test(icon.match(/<svg[^>]*>/)?.[0] ?? '');
    registry.set(name, { body, viewBox, filled });
  } else {
    registry.set(name, icon);
  }
  listeners.forEach(l => l(name));
}

export function getIcon(name: string): IconDefinition | undefined {
  return registry.get(name);
}

export function iconNames(): string[] {
  return [...registry.keys()].sort();
}

/** Full `<svg>` markup for an icon — handy for frameworks or string templates. */
export function iconSvg(name: string, { size = 24, strokeWidth = 2, label }: { size?: number | string; strokeWidth?: number; label?: string } = {}): string {
  const icon = registry.get(name);
  if (!icon) return '';
  const a11y = label ? `role="img" aria-label="${label.replace(/"/g, '&quot;')}"` : 'aria-hidden="true"';
  const paint = icon.filled
    ? 'fill="currentColor"'
    : `fill="none" stroke="currentColor" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"`;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="${icon.viewBox ?? '0 0 24 24'}" ${paint} ${a11y}>${icon.body}</svg>`;
}

/** @internal re-render <hn-icon> elements when an icon is registered late. */
export function onIconRegistered(listener: (name: string) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
